import React, { useState, useEffect } from 'react';
import axios from 'axios';

function WalletAddressManagement() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    currency: 'USDT',
    network: 'ERC20',
    address: '',
    memo: '',
    min_deposit: 0,
    is_active: true
  });

  const currencies = ['USDT', 'BTC', 'ETH', 'BNB'];
  const networks = {
    USDT: ['ERC20', 'BEP20', 'TRC20'],
    BTC: ['ERC20', 'BEP20'],
    ETH: ['ERC20', 'BEP20'],
    BNB: ['BEP20']
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trcsite.net/api/admin/wallet-addresses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data.addresses);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching wallet addresses:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingAddress) {
        await axios.post(`https://trcsite.net/api/admin/wallet-addresses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('https://trcsite.net/api/admin/wallet-addresses', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      alert('Wallet address saved successfully!');
      setFormData({
        currency: 'USDT',
        network: 'ERC20',
        address: '',
        memo: '',
        min_deposit: 0,
        is_active: true
      });
      setEditingAddress(null);
      fetchAddresses();
    } catch (error) {
      console.error('Error saving wallet address:', error);
      alert('Error saving wallet address: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      currency: address.currency,
      network: address.network,
      address: address.address,
      memo: address.memo || '',
      min_deposit: address.min_deposit,
      is_active: address.is_active
    });
  };

  const handleToggleStatus = async (address) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`https://trcsite.net/api/admin/wallet-addresses/${address.id}/status`, {
        is_active: !address.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Wallet address ${!address.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchAddresses();
    } catch (error) {
      console.error('Error toggling wallet address status:', error);
      alert('Error updating wallet address status: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCurrencyChange = (currency) => {
    setFormData({
      ...formData,
      currency,
      network: networks[currency]?.[0] || 'ERC20'
    });
  };

  if (loading) {
    return <div className="loading">Loading wallet addresses...</div>;
  }

  return (
    <div className="wallet-address-management">
      <div className="section-header">
        <h2>💰 Wallet Address Management</h2>
        <p>Manage cryptocurrency wallet addresses for user deposits</p>
      </div>

      {/* Add/Edit Form */}
      <div className="form-section" style={{ marginBottom: '30px' }}>
        <h3>{editingAddress ? 'Edit' : 'Add New'} Wallet Address</h3>
        <form onSubmit={handleSubmit} className="address-form">
          <div className="form-row">
            <div className="form-group">
              <label>Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                required
              >
                {currencies.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Network</label>
              <select
                value={formData.network}
                onChange={(e) => setFormData({...formData, network: e.target.value})}
                required
              >
                {(networks[formData.currency] || []).map(network => (
                  <option key={network} value={network}>{network}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Minimum Deposit</label>
              <input
                type="number"
                step="0.00000001"
                value={formData.min_deposit}
                onChange={(e) => setFormData({...formData, min_deposit: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Wallet Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Enter wallet address"
              required
            />
          </div>

          <div className="form-group">
            <label>Memo/Tag (Optional)</label>
            <input
              type="text"
              value={formData.memo}
              onChange={(e) => setFormData({...formData, memo: e.target.value})}
              placeholder="Enter memo/tag if required"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              Active
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {editingAddress ? 'Update' : 'Add'} Address
            </button>
            {editingAddress && (
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  setEditingAddress(null);
                  setFormData({
                    currency: 'USDT',
                    network: 'ERC20',
                    address: '',
                    memo: '',
                    min_deposit: 0,
                    is_active: true
                  });
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Addresses List */}
      <div className="addresses-list">
        <h3>Managed Wallet Addresses</h3>
        {addresses.length === 0 ? (
          <div className="no-data">No wallet addresses configured</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Network</th>
                  <th>Address</th>
                  <th>Memo</th>
                  <th>Min Deposit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map(address => (
                  <tr key={address.id}>
                    <td>
                      <span className="currency-badge">{address.currency}</span>
                    </td>
                    <td>
                      <span className="network-badge">{address.network}</span>
                    </td>
                    <td className="address-cell">
                      <code>{address.address}</code>
                    </td>
                    <td>
                      {address.memo || '-'}
                    </td>
                    <td className="amount-cell">
                      {address.min_deposit} {address.currency}
                    </td>
                    <td>
                      <span className={`status-badge ${address.is_active ? 'status-active' : 'status-inactive'}`}>
                        {address.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(address)}
                          className="btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleToggleStatus(address)}
                          className={address.is_active ? 'btn-warning' : 'btn-success'}
                          title={address.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {address.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(address.address)}
                          className="btn-secondary"
                          title="Copy Address"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style jsx>{`
        .wallet-address-management {
          padding: 20px;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .form-group {
          margin-bottom: 15px;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 5px;
          color: #b0b8c4;
          font-weight: 500;
        }
        
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px;
          background: #2a3b52;
          border: 1px solid #344258;
          border-radius: 4px;
          color: #ffffff;
        }
        
        .checkbox-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        
        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }
        
        .address-cell code {
          background: #2a3b52;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          word-break: break-all;
        }
        
        .currency-badge,
        .network-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .currency-badge {
          background: #2962ff;
          color: white;
        }
        
        .network-badge {
          background: #00c853;
          color: white;
        }
        
        .action-buttons {
          display: flex;
          gap: 5px;
        }
        
        .action-buttons button {
          padding: 6px 10px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }
        
        .btn-primary { background: #2962ff; color: white; }
        .btn-secondary { background: #6c757d; color: white; }
        .btn-success { background: #00c853; color: white; }
        .btn-warning { background: #ffa726; color: white; }
        .btn-edit { background: #17a2b8; color: white; }
      `}</style>
    </div>
  );
}

export default WalletAddressManagement;